import { getGameMapById, getReplay } from "@dailywars/core/db";
import {
  applyMove,
  createInitialState,
  mapIdToCalendarDate,
  mapIdToVariant,
} from "@dailywars/core/engine";
import type { GameState, Move } from "@dailywars/core/engine";
import type { MapVariant } from "@dailywars/core/engine/maps/mapId";
import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";
import { flattenReplayMoves } from "@dailywars/core/replay/flattenReplayMoves";
import { createServerFn } from "@tanstack/react-start";
import * as z from "zod";

import { GUEST_DISPLAY_NAME } from "#/lib/playerDisplayName";
import { setRouteAction } from "#/lib/requestLogger.server";
import { replayIdSchema } from "#/lib/routeParams";
import { resolveLeaderboardDisplayNames } from "#/server/game/leaderboardPlayers";

const loadReplayInputSchema = z.object({
  replayId: replayIdSchema,
});

export type ReplayPageData = {
  locked: boolean;
  initialState: GameState;
  moves: Move[];
  mapId: string;
  mapDate: string;
  variant: MapVariant;
  playerId: string;
  displayName: string;
  finalServerDayNumber: number;
};

export const loadReplay = createServerFn({ method: "GET" })
  .validator(loadReplayInputSchema)
  .handler(async ({ data }): Promise<ReplayPageData | null> => {
    setRouteAction("replay_page");
    const replay = await getReplay(data.replayId);
    if (!replay) return null;

    const { metadata, rounds } = replay;
    const map = await getGameMapById(metadata.mapId);
    const initialState = createInitialState(map);
    const names = await resolveLeaderboardDisplayNames([metadata.playerId]);
    const displayName = names.get(metadata.playerId) ?? GUEST_DISPLAY_NAME;
    const mapDate = metadata.mapDate ?? mapIdToCalendarDate(metadata.mapId);
    const variant = mapIdToVariant(metadata.mapId);
    const locked = mapDate === toUtcDateString(new Date());

    if (locked) {
      return {
        locked: true,
        initialState,
        moves: [],
        mapId: metadata.mapId,
        mapDate,
        variant,
        playerId: metadata.playerId,
        displayName,
        finalServerDayNumber: 1,
      };
    }

    const moves = flattenReplayMoves(rounds);
    let finalState = initialState;
    for (const move of moves) {
      finalState = applyMove(finalState, move).state;
    }

    return {
      locked: false,
      initialState,
      moves,
      mapId: metadata.mapId,
      mapDate,
      variant,
      playerId: metadata.playerId,
      displayName,
      finalServerDayNumber: finalState.currentDay,
    };
  });
